import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { breakpoints, belowBreakpoint } from '../utils';
import { useBreakpoint } from '../customHooks/breakpoints';
import { BookButtons } from './BookButtons';
import { Availability } from './Availability';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _, { isNil } from 'lodash';
import Drawer from './Drawer';
import AdmissionPolicyModal from './AdmissionPolicyModal';
import { ConfigContext } from '../ConfigContext';
import { PriceList } from '../../shared/helpers/PriceHelper';
const Time = styled.div`
  font-weight: 700;
  margin-left: 1rem !important;

  ${belowBreakpoint(breakpoints.md)} {
    flex: 1;
    margin-left: 0px !important;
    font-weight: 500;
  }
`;

// It would be better if the activity list text was all justified to the left and the size of all of the activity divs
// was the width of the longest activity div in the table, but I don't know how to do that. So for now, all the text
// is justified right.
const ActivityList = styled.div`
  @media (max-width: 400px) {
    display: none;
  }

  & > ul {
    list-style: none;
  }

  & > ul > li {
    text-align: right;
  }
`;

// TODO no obvious way to type the props for these styled-components
const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 0.3rem 0;
  cursor: pointer;
  user-select: none;
  flex-wrap: wrap;
  min-height: 55px;

  & > div {
    margin-left: 5px;
    margin-right: 5px;
  }

  &:nth-child(2n + 1) {
    background-color: ${(props) => props.customStyle?.odd?.backgroundColor || 'inherit'};
  }

  &:nth-child(2n) {
    background-color: ${(props) => props.customStyle?.even?.backgroundColor || '#ccc'};
  }

  ${belowBreakpoint(breakpoints.md)} {
    & > div {
      margin-left: 2px;
      margin-right: 2px;
    }

    padding: 0.75rem;
  }
`;

const Name = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;

  ${belowBreakpoint(breakpoints.md)} {
    font-weight: 700;
  }
`;

const Price = styled.div`
  min-width: 48px;
  font-weight: 700;
`;

const ExpandButton = styled.button.attrs((props) => ({
  'data-tooltip': props.title,
}))`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  color: #000;

  &:hover {
    color: #000;
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    white-space: nowrap;
    font-size: 0.75rem;
    line-height: 1;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.1s ease-in-out;
    z-index: 10;
  }

  &:hover::after {
    opacity: 1;
  }
`;
// TODO: there is a better way of doing the css for the columns in the gridrow, it's called grid-row
const SportActivityLocation = styled.div`
  min-width: 120px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;

  ${belowBreakpoint(breakpoints.md)} {
    min-width: 200px;
    max-width: none;
    width: 100%;
  }
`;

/**
 * @param {PriceList} price
 * @param {boolean} showPrices
 * @returns {React.ReactNode}
 */
const renderPrice = (price, showPrices) => {
  if (showPrices) {
    return <Price>{price.opportunityPrice}</Price>;
  }
  return null;
};

const Gridrow = ({
  price,
  showPrices,
  eventSeries,
  description,
  shouldShowNoAvailability,
  shouldShowExactAvailability,
  shouldNotShowDeepLinkButton,
  memberDeepLinks,
  subEvent,
  checkoutBaseUrl,
  uniqueId,
  name,
  bookableProviders,
  checkoutTheme,
  admissionsPolicyUrl,
  showActivityTypes,
  isDescriptionOpen,
  onToggle,
  showSportActivityLocation,
  sportActivityLocationLock,
  ...props
}) => {
  // local modal state for admission policy modal
  const [modal, setModal] = useState({ isOpen: false, props: {} });
  const config = useContext(ConfigContext);

  const deviceWidth = useBreakpoint();

  const hasDescriptionDrawer =
    !isNil(eventSeries.description) ||
    !isNil(eventSeries.description) ||
    (showPrices && price.opportunityPrice);

  const sportActivityLocationString =
    showSportActivityLocation && _.isNil(sportActivityLocationLock)
      ? (subEvent['_imin:sal'] || []).join(', ')
      : null;

  const changeModalState = (newState) => {
    const body = document.querySelector('body');
    if (!newState.isOpen) {
      body.style.overflow = 'auto';
    } else {
      body.style.overflow = 'hidden';
    }

    setModal(newState);
  };

  if (deviceWidth === 'sm' || deviceWidth === 'md') {
    return (
      <Row onClick={onToggle} customStyle={config.customStyleForStyledComponents?.gridRow}>
        <Name>{name}</Name>
        <div
          style={{
            marginTop: 4,
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Time>{props.time}</Time>
          {renderPrice(price, showPrices)}
        </div>
        {showSportActivityLocation && _.isNil(sportActivityLocationLock) && (
          <div
            style={{
              marginTop: 4,
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              alignItems: 'left',
            }}
          >
            <SportActivityLocation>{sportActivityLocationString}</SportActivityLocation>
          </div>
        )}
        <div
          style={{
            marginTop: 4,
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            alignItems: 'center',
          }}
        >
          {!shouldShowNoAvailability && (
            <Availability
              subEvent={subEvent}
              shouldShowExactAvailability={shouldShowExactAvailability}
            />
          )}
          <ExpandButton
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            title={isDescriptionOpen ? 'Hide details' : 'Show details'}
            customStyle={config.customStyle?.button}
          >
            <FontAwesomeIcon
              icon={isDescriptionOpen ? 'chevron-up' : 'chevron-down'}
              style={{
                height: 20,
                width: 20,
                marginRight: 10,
                transition: 'all 0.25s',
              }}
            />
          </ExpandButton>
        </div>
        {hasDescriptionDrawer && isDescriptionOpen && (
          <Drawer
            shouldShowNoAvailability={shouldShowNoAvailability}
            shouldNotShowDeepLinkButton={shouldNotShowDeepLinkButton}
            subEvent={subEvent}
            eventSeries={eventSeries}
            provider={props.provider}
            memberDeepLinks={memberDeepLinks}
            price={price}
            showPrices={showPrices}
            description={description}
            checkoutBaseUrl={checkoutBaseUrl}
            checkoutTheme={checkoutTheme}
            uniqueId={uniqueId}
            bookableProviders={bookableProviders}
            setModal={changeModalState}
            admissionsPolicyUrl={admissionsPolicyUrl}
          />
        )}
        {modal.isOpen && (
          <AdmissionPolicyModal changeModalState={changeModalState} modalProps={modal.props} />
        )}
      </Row>
    );
  }
  return (
    <Row onClick={onToggle} customStyle={config.customStyleForStyledComponents?.gridRow}>
      <Time>{props.time}</Time>
      <Name>{name}</Name>
      {showActivityTypes && (
        <ActivityList>
          <ul>
            {props.activityList.map((_) => (
              <li key={_.name}>{_.name}</li>
            ))}
          </ul>
        </ActivityList>
      )}
      {renderPrice(price, showPrices)}
      {showSportActivityLocation && _.isNil(sportActivityLocationLock) && (
        <SportActivityLocation>{sportActivityLocationString}</SportActivityLocation>
      )}
      {!shouldShowNoAvailability && (
        <Availability
          subEvent={subEvent}
          shouldShowExactAvailability={shouldShowExactAvailability}
        />
      )}
      {!shouldShowNoAvailability && (
        <BookButtons
          subEvent={subEvent}
          eventSeries={eventSeries}
          checkoutBaseUrl={checkoutBaseUrl}
          uniqueId={uniqueId}
          provider={props.provider}
          bookableProviders={bookableProviders}
          memberDeepLinks={memberDeepLinks}
          checkoutTheme={checkoutTheme}
          setModal={changeModalState}
          admissionsPolicyUrl={admissionsPolicyUrl}
        />
      )}
      <ExpandButton
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        title={isDescriptionOpen ? 'Hide details' : 'Show details'}
        customStyle={config.customStyle?.button}
      >
        <FontAwesomeIcon
          icon={isDescriptionOpen ? 'chevron-up' : 'chevron-down'}
          style={{
            height: 20,
            width: 20,
            marginRight: 10,
            transition: 'all 0.25s',
            visibility: hasDescriptionDrawer ? undefined : 'hidden',
          }}
        />
      </ExpandButton>
      {hasDescriptionDrawer && isDescriptionOpen && (
        <Drawer
          shouldShowNoAvailability={shouldShowNoAvailability}
          shouldNotShowDeepLinkButton={shouldNotShowDeepLinkButton}
          subEvent={subEvent}
          provider={props.provider}
          memberDeepLinks={memberDeepLinks}
          price={price}
          showPrices={showPrices}
          description={description}
          checkoutBaseUrl={checkoutBaseUrl}
          checkoutTheme={checkoutTheme}
          uniqueId={uniqueId}
          bookableProviders={bookableProviders}
        />
      )}
      {modal.isOpen && (
        <AdmissionPolicyModal changeModalState={changeModalState} modalProps={modal.props} />
      )}
    </Row>
  );
};

Gridrow.propTypes = {
  name: PropTypes.string,
  time: PropTypes.string,
  activityList: PropTypes.array,
  description: PropTypes.string,
  eventSeries: PropTypes.object,
  subEvent: PropTypes.object,
  provider: PropTypes.string,
  price: PropTypes.shape({
    opportunityPrice: PropTypes.string,
    offers: PropTypes.arrayOf(PropTypes.object),
  }),
  uniqueId: PropTypes.string,
  shouldShowExactAvailability: PropTypes.bool,
  shouldShowNoAvailability: PropTypes.bool,
  shouldNotShowDeepLinkButton: PropTypes.bool,
  memberDeepLinks: PropTypes.object,
  checkoutBaseUrl: PropTypes.string,
  bookableProviders: PropTypes.arrayOf(PropTypes.string),
  checkoutTheme: PropTypes.string,
  admissionsPolicyUrl: PropTypes.string,
  showActivityTypes: PropTypes.bool,
  showPrices: PropTypes.bool,
  isDescriptionOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  showSportActivityLocation: PropTypes.bool,
  sportActivityLocationLock: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
};

export default Gridrow;
